/**
 * `lookup` on a PostgreSQL array column — the one rule both filter emitters
 * apply: the renderer's `filterWhereBuilder.ts` (Visual Query Editor chip,
 * Data Profile) and the Table Explorer generators' `queryUtils.ts`.
 *
 * `lookup` is an input mode (the foreign-key row picker), not an SQL
 * operator. On a scalar column the picked key is compared with `=`; an array
 * column (`integer[]`) has no `=` with one element (`operator does not exist:
 * integer[] = integer`), so the key is tested for membership instead — the
 * `array_contains` emit, `<value> = ANY(<col>)`. The rule keys on the compared
 * column's data type: the chip passes it as `valueDataType`, Table Explorer's
 * provider as the generator's `columnTypes`. Only PostgreSQL has array
 * columns; every other engine keeps the operator.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { FilterCondition } from '../../core/types/workspaceTypes';

type FilterOperator = FilterCondition['operator'];

/**
 * An array type as PostgreSQL's `format_type` spells it: `integer[]`,
 * `character varying(10)[]`, `text[][]`. The catalog's `udt_name` spelling
 * (`_int4`) is not an array type here — callers pass the formatted type.
 */
export function isArrayDataType(dataType: string | undefined): boolean {
  return typeof dataType === 'string' && /(\s*\[\s*\])+$/u.test(dataType);
}

/** A filter set that holds a `lookup` condition (the only operator this rule rewrites). */
export function hasLookupCondition(filters: readonly { readonly conditions: readonly FilterCondition[] }[] | undefined): boolean {
  return (filters ?? []).some((g) => g.conditions.some((c) => c.operator === 'lookup'));
}

/** The operator the emitters write for `operator` on a column of `dataType`. */
export function emittedFilterOperator(
  operator: FilterOperator,
  engine: DatabaseEngineId | undefined,
  dataType: string | undefined,
): FilterOperator {
  return operator === 'lookup' && engine === 'pgsql' && isArrayDataType(dataType) ? 'array_contains' : operator;
}
